import React, { useState } from "react";
import SEO from "../components/SEO";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Calculator, Clock, DollarSign, TrendingDown } from "lucide-react";

const INPUTS = [
  { id: "docsPerMonth", label: "Documents drafted per month (CAPAs, SOPs, deviations, etc.)", min: 1, max: 100, default: 10 },
  { id: "hoursPerDoc", label: "Average hours spent drafting each document manually", min: 0.5, max: 20, default: 4 },
  { id: "hourlyRate", label: "Hourly cost of QA staff time (USD)", min: 25, max: 300, default: 75 },
  { id: "revisionsPerDoc", label: "Average revision cycles per document", min: 1, max: 10, default: 3 },
];

export default function ROICalculator() {
  const [values, setValues] = useState({
    docsPerMonth: 10,
    hoursPerDoc: 4,
    hourlyRate: 75,
    revisionsPerDoc: 3,
  });
  const [calculated, setCalculated] = useState(false);

  const handleChange = (id, val) => {
    setValues((prev) => ({ ...prev, [id]: Number(val) }));
    setCalculated(false);
  };

  // Calculations
  const currentMonthlyHours = values.docsPerMonth * values.hoursPerDoc * values.revisionsPerDoc;
  const currentMonthlyCost = currentMonthlyHours * values.hourlyRate;

  // LabComplyx assumptions: 70% reduction in drafting time, 50% fewer revision cycles
  const optimizedHours = values.docsPerMonth * (values.hoursPerDoc * 0.3) * (values.revisionsPerDoc * 0.5);
  const optimizedCost = optimizedHours * values.hourlyRate;

  const monthlyHoursSaved = currentMonthlyHours - optimizedHours;
  const monthlyCostSaved = currentMonthlyCost - optimizedCost;
  const annualCostSaved = monthlyCostSaved * 12;
  const annualHoursSaved = monthlyHoursSaved * 12;

  return (
    <>
      <SEO
        title="Compliance ROI Calculator for Regulated Labs | LabComplyx"
        description="Estimate the cost of documentation backlog, inspection exposure, and reactive QA work versus proactive compliance support for your lab."
        keywords="lab compliance ROI calculator, documentation time savings, CAPA drafting cost, QA staff efficiency, compliance documentation ROI"
        canonical="https://labcomplyx.com/roi-calculator"
        ogUrl="https://labcomplyx.com/roi-calculator"
        breadcrumbs={[
          { name: "Home", url: "https://labcomplyx.com/" },
          { name: "Resources", url: "https://labcomplyx.com/resources" },
          { name: "ROI Calculator", url: "https://labcomplyx.com/roi-calculator" },
        ]}
      />

      {/* Hero */}
      <section className="pt-28 pb-12 bg-gradient-to-b from-muted/40 to-background">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-primary bg-primary/8 px-4 py-1.5 rounded-full mb-5">
            <Calculator className="w-3.5 h-3.5" /> ROI Calculator
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">
            Discover Your Documentation Savings
          </h1>
          <p className="text-lg text-muted-foreground max-w-xl mx-auto">
            See how much time and cost your lab can recover by replacing manual,
            ad hoc documentation with governed, audit-ready workflows.
          </p>
        </div>
      </section>

      {/* Calculator */}
      <section className="py-12 bg-background">
        <div className="max-w-3xl mx-auto px-6">
          <div className="bg-card border border-border rounded-2xl p-8 shadow-sm space-y-8">
            <h2 className="text-xl font-semibold">Your Current Documentation Workload</h2>

            {INPUTS.map((input) => (
              <div key={input.id}>
                <label className="block text-sm font-medium mb-2">{input.label}</label>
                <div className="flex items-center gap-4">
                  <input
                    type="range"
                    min={input.min}
                    max={input.max}
                    step={input.id === "hoursPerDoc" ? 0.5 : 1}
                    value={values[input.id]}
                    onChange={(e) => handleChange(input.id, e.target.value)}
                    className="flex-1 accent-primary"
                  />
                  <span className="w-16 text-center font-semibold text-primary bg-primary/8 rounded-lg py-1 text-sm">
                    {input.id === "hourlyRate" ? `$${values[input.id]}` : values[input.id]}
                  </span>
                </div>
              </div>
            ))}

            <Button
              className="w-full gap-2 bg-primary hover:bg-primary/90"
              onClick={() => setCalculated(true)}
            >
              <Calculator className="w-4 h-4" />
              Calculate My Savings
            </Button>
          </div>

          {/* Results */}
          {calculated && (
            <div className="mt-8 space-y-4">
              <h2 className="text-xl font-semibold text-center mb-6">Your Estimated Savings with LabComplyx</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-primary/5 border border-primary/20 rounded-xl p-6 text-center">
                  <Clock className="w-6 h-6 text-primary mx-auto mb-2" />
                  <div className="text-3xl font-bold text-primary">{Math.round(annualHoursSaved).toLocaleString()}</div>
                  <div className="text-sm text-muted-foreground mt-1">Hours saved per year</div>
                </div>
                <div className="bg-secondary/10 border border-secondary/20 rounded-xl p-6 text-center">
                  <DollarSign className="w-6 h-6 text-secondary mx-auto mb-2" />
                  <div className="text-3xl font-bold text-secondary">${Math.round(annualCostSaved).toLocaleString()}</div>
                  <div className="text-sm text-muted-foreground mt-1">Estimated annual savings</div>
                </div>
                <div className="bg-accent border border-accent-foreground/10 rounded-xl p-6 text-center">
                  <TrendingDown className="w-6 h-6 text-accent-foreground mx-auto mb-2" />
                  <div className="text-3xl font-bold text-accent-foreground">{Math.round(monthlyCostSaved).toLocaleString()}</div>
                  <div className="text-sm text-muted-foreground mt-1">Monthly cost reduction (USD)</div>
                </div>
              </div>

              <div className="bg-muted/50 rounded-xl p-5 text-sm text-muted-foreground text-center mt-4">
                Based on illustrative drafting-time and revision-cycle reductions — actual results vary by lab and document complexity.
              </div>

              <div className="text-center pt-4">
                <p className="text-sm text-muted-foreground mb-4">Ready to capture these savings for your lab?</p>
                <Link to="/validation-intake">
                  <Button className="gap-2 bg-primary hover:bg-primary/90">
                    Book a Free Compliance Review
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Bottom links */}
      <section className="py-10 bg-muted/30 border-t border-border">
        <div className="max-w-3xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
          <span>Explore more resources for regulated labs</span>
          <div className="flex gap-4">
            <Link to="/resources" className="text-primary hover:underline flex items-center gap-1">Resources <ArrowRight className="w-3 h-3" /></Link>
            <Link to="/solutions" className="text-primary hover:underline flex items-center gap-1">Solutions <ArrowRight className="w-3 h-3" /></Link>
          </div>
        </div>
      </section>
    </>
  );
}